import { createContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

export const ThemeContext = createContext();

/**
 * Paths that always use light theme (excluded from app theme).
 * Empty this array to include login/register (and any other) pages in the app theme again.
 */
export const THEME_EXCLUDED_PATHS = ["/login", "/signup"];

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const pathname = location?.pathname ?? "";

  // Get theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme || "dark";
  });

  // On excluded paths (e.g. login/register), force light theme; otherwise use user's theme
  const isExcludedFromTheme = THEME_EXCLUDED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(path + "/")
  );
  const effectiveTheme = isExcludedFromTheme ? "light" : theme;

  // Apply effective theme to document root (instant switch, no staggered transitions)
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", effectiveTheme);
    if (!isExcludedFromTheme) {
      localStorage.setItem("theme", theme);
    }
    requestAnimationFrame(() => {
      root.removeAttribute("data-theme-transitioning");
    });
  }, [effectiveTheme, isExcludedFromTheme, theme]);

  // Listen for system theme changes (optional)
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      if (!localStorage.getItem("theme")) {
        setTheme(e.matches ? "dark" : "light");
      }
    };

    // Set initial theme based on system preference if no saved preference
    if (!localStorage.getItem("theme")) {
      setTheme(mediaQuery.matches ? "dark" : "light");
    }

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleTheme = () => {
    const root = document.documentElement;
    root.setAttribute("data-theme-transitioning", "true");
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

